import React, {useState} from 'react';
import {
  Formik, Form, useField, useFormikContext,
} from 'formik';
import * as yup from 'yup';

import {
  TextArea, Checkbox, LabelAndErrors,
} from '../../../components/Forms';
import {useAppContext} from '../../../hooks';
import StepNavigation from '../StepNavigation';

const formatWithCommas = (value) => {
  if (!value && value !== 0) return '';
  const num = String(value).replace(/,/g, '');
  if (num === '' || Number.isNaN(Number(num))) return String(value);
  return Number(num).toLocaleString('en-US');
};

const parseCommas = (value) => {
  const num = String(value).replace(/,/g, '');
  return num === '' ? '' : num;
};

const CurrencyInput = ({
  name, label, placeholder, onBlur,
}) => {
  const [field, , helpers] = useField(name);
  const handleBlur = (e) => {
    field.onBlur(e);
    if (onBlur) onBlur({...e, target: {...e.target, value: parseCommas(e.target.value)}});
  };
  const handleChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    helpers.setValue(rawValue);
  };
  return (
    <LabelAndErrors label={label} name={name}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">$</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          id={name}
          name={field.name}
          value={formatWithCommas(field.value)}
          onChange={handleChange}
          placeholder={placeholder}
          onBlur={handleBlur}
          className="text-sm rounded-lg border border-gray-300 bg-white hover:border-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 pl-7 pr-3 py-2 w-full focus:outline-none text-gray-800 placeholder-gray-400"
        />
      </div>
    </LabelAndErrors>
  );
};

const CompensationComments = ({onEdit}) => {
  const {values} = useFormikContext();
  const hasCompensation = values.salary || values.stock || values.bonus;

  return (
    <div
      className="grid transition-all duration-300 ease-in-out"
      style={{
        gridTemplateRows: hasCompensation ? '1fr' : '0fr',
        opacity: hasCompensation ? 1 : 0,
      }}
    >
      <div className="overflow-hidden p-1 -m-1">
        <TextArea
          label="Compensation comments"
          name="compComments"
          autoComplete="off"
          onBlur={(e) => onEdit?.({compComments: e.target.value})}
        />
      </div>
    </div>
  );
};

const DifficultySlider = ({name, label, onSelect}) => {
  const [field, , helpers] = useField(name);
  const value = field.value ?? 3;

  const handleChange = (e) => {
    const newValue = Number(e.target.value);
    helpers.setValue(newValue);
    if (onSelect) onSelect(newValue);
  };

  // Calculate fill percentage (value 1-5 maps to 0-100%)
  const fillPercent = ((value - 1) / 4) * 100;

  return (
    <div className="space-y-3 mb-3">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <div className="relative flex items-center">
          <input
            type="range"
            min="1"
            max="5"
            step="1"
            value={value}
            onChange={handleChange}
            className="difficulty-slider relative z-10 w-full h-5 bg-transparent rounded-lg appearance-none cursor-pointer"
          />
          <div className="absolute inset-x-0 h-1 bg-indigo-100 border-t border-indigo-200 rounded-lg pointer-events-none" />
          <div
            className="absolute left-0 h-1 bg-indigo-500 border-t border-indigo-600 rounded-lg pointer-events-none"
            style={{width: `${fillPercent}%`}}
          />
          <div className="absolute inset-x-0 h-1 px-0.5 flex justify-between items-center pointer-events-none">
            {[1, 2, 3, 4, 5].map((tick) => (
              <div
                key={tick}
                className="w-0.5 h-1 bg-indigo-400"
                style={{opacity: tick <= value ? 0 : 1}}
              />
            ))}
          </div>
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-sm text-gray-400 select-none">Easy</span>
          <span className="text-sm text-gray-400 select-none">Difficult</span>
        </div>
      </div>
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx>
        {`
        .difficulty-slider::-webkit-slider-runnable-track {
          background: transparent;
        }
        .difficulty-slider::-moz-range-track {
          background: transparent;
        }
        .difficulty-slider::-webkit-slider-thumb {
          appearance: none;
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 9999px;
          background-color: #6366f1;
          cursor: pointer;
          box-shadow: 0 0 0 5px white, 0 0 0 6px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.5), 0 10px 15px rgba(0,0,0,0.4), 0 20px 30px rgba(0,0,0,0.3);
        }
        .difficulty-slider::-moz-range-thumb {
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 9999px;
          background-color: #6366f1;
          border: 0;
          cursor: pointer;
          box-shadow: 0 0 0 5px white, 0 0 0 6px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.5), 0 10px 15px rgba(0,0,0,0.4), 0 20px 30px rgba(0,0,0,0.3);
        }
      `}
      </style>
    </div>
  );
};

const schema = yup.object({
  overview: yup.string().min(100, 'This question is essential for others learning about the interview process. Please provide a few sentences.').required('Required'),
  difficulty: yup.number().min(1).max(5),
  salary: yup.number().positive().nullable(),
  stock: yup.number().positive().nullable(),
  bonus: yup.number().positive().nullable(),
  compComments: yup.string(),
  tips: yup.string().min(100, 'Please provide a few sentences on how to prepare.').required('Required'),
  companyCulture: yup.string().min(50, 'Please share a bit about the team or company culture.').required('Required'),
  canFollowUp: yup.bool(),
});

const ReflectionsStep = ({
  draft, onNext, onBack, onEdit, isSubmitting,
}) => {
  const {hasSubscription, hasIxpAccess} = useAppContext();
  // Hack to keep initialValues stable across renders,
  // preventing enableReinitialize from resetting form values
  const [initialValues] = useState(() => ({
    overview: draft.overview || '',
    difficulty: draft.difficulty ?? 3,
    salary: draft.salary || '',
    stock: draft.stock || '',
    bonus: draft.bonus || '',
    compComments: draft.compComments || '',
    tips: draft.tips || '',
    companyCulture: draft.companyCulture || '',
    canFollowUp: draft.canFollowUp ?? true,
  }));

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => onNext(values)}
      enableReinitialize
    >
      {() => (
        <Form className="flex flex-col gap-y-1">
          <TextArea
            label="Describe your overall interview process. What went well? What didn’t go well?"
            name="overview"
            placeholder="The process was more rigorous than anything I’d done at other big tech companies, but the recruiter was actually accurate and helpful on what to prepare, which is rare and I really appreciated that. A few things caught me off guard: one round had..."
            autoComplete="off"
            onBlur={(e) => onEdit?.({overview: e.target.value})}
            rows={5}
            required
          />

          <DifficultySlider
            label="How does this interview compare to other interviews you've done?"
            name="difficulty"
            onSelect={(val) => onEdit?.({difficulty: val})}
          />

          {(draft.result === 'accepted' || draft.result === 'declined') && (
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">What was the offered compensation?</h3>
              <div className="grid grid-cols-3 gap-3">
                <CurrencyInput
                  label="Base salary"
                  name="salary"
                  placeholder="USD"
                  onBlur={(e) => onEdit?.({salary: e.target.value})}
                />
                <CurrencyInput
                  label="Stock"
                  name="stock"
                  placeholder="USD"
                  onBlur={(e) => onEdit?.({stock: e.target.value})}
                />
                <CurrencyInput
                  label="Bonus"
                  name="bonus"
                  placeholder="USD"
                  onBlur={(e) => onEdit?.({bonus: e.target.value})}
                />
              </div>
              <CompensationComments onEdit={onEdit} />
            </div>
          )}

          <TextArea
            label="What would you tell a friend to prepare for this interview?"
            name="tips"
            placeholder="Were you surprised by anything? What advice would you give someone to prepare?"
            autoComplete="off"
            onBlur={(e) => onEdit?.({tips: e.target.value})}
            required
          />

          <TextArea
            label="What stood out to you about the team or company culture?"
            name="companyCulture"
            placeholder="What was the overall vibe? Any red flags?"
            autoComplete="off"
            onBlur={(e) => onEdit?.({companyCulture: e.target.value})}
            required
          />

          <Checkbox
            label="You can contact me about my interview experience"
            name="canFollowUp"
            className="border border-gray-300 rounded-lg pt-4 px-4 pb-3"
            onChange={(e) => onEdit?.({canFollowUp: e.target.checked})}
          />

          {!hasSubscription && !hasIxpAccess && (
            <p className="text-sm text-gray-600 rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3 mt-2">
              Trial access only unlocks if your experience passes
              our quality review, so give it a quick check before you submit.
            </p>
          )}

          <StepNavigation onBack={onBack} nextLabel="Submit" isSubmitting={isSubmitting} />
        </Form>
      )}
    </Formik>
  );
};

export default ReflectionsStep;
